import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { useBanner } from '@tq/ui-components';

import { useConsultation, useResponses, useSubmitResponse } from 'api';
import { BANNER_TIMEOUT } from 'config';
import { useNavigate } from 'hooks';
import {
  permissionValuesState,
  reasonForLateResponseState,
  responseTypeIdIsConsultedInErrorState,
  useResponseFilesState,
} from 'store';
import { isOverdue, isRpc, isInspection } from 'tools';
import { useResponseTypeDescription } from './useResponseTypeDescription';
import { GetResponsesConsultationResponse } from '../../../api/responses/types/GetResponsesConsultationResponse';
import { UseResponseReturn } from './types/UseResponseReturn';
import { canSubmitStandard } from './fragments/canSubmitStandard';
import { canSubmitAnswers } from './fragments/canSubmitAnswers';
import { sortAnswers } from './fragments/sortAnswers';
import { getAnswers } from './fragments/getAnswers';
import { getPastResponses } from './fragments/getPastResponses';
import { useResetResponseFormState } from './useResetResponseFormState';

/**
 * The `useResponse` function is a custom hook in TypeScript that handles the logic for submitting a
 * response to a consultation, including handling different response types and validation checks.
 * @param {number} consultationId - The `consultationId` parameter is the unique identifier of the
 * consultation for which the response is being handled.
 * @returns The function `useResponse` returns an object with the following properties:
 */
export const useResponse = (consultationId: number): UseResponseReturn => {
  const { t } = useTranslation('consultation_response');
  const { getResponseTypeDescription } = useResponseTypeDescription();
  const banner = useBanner();
  const { navigateToDashboard } = useNavigate();

  const { consulteeId, consulteeName, consulteeOrganisationIds } =
    useRecoilValue(permissionValuesState);
  const responseTypeIdIsConsultedInError = useRecoilValue(
    responseTypeIdIsConsultedInErrorState
  );
  const { responseTypeId, responseDetail, responseFiles, supportingFiles, requiredFiles } =
    useRecoilValue(useResponseFilesState);

  const { reasonForLateReasonIsOther, reasonForLateResponseId, reasonForLateResponseDetail } =
    useRecoilValue(reasonForLateResponseState);

  const { resetForm } = useResetResponseFormState();

  const { data: consultation, isLoading: isLoadingConsultation } =
    useConsultation(consultationId);
  const { data: responsesData, isLoading: isLoadingResponse } = useResponses(consultationId);
  const { mutate: submitResponseApi } = useSubmitResponse();

  const isRpcResponse = isRpc(consultation);
  const isInspectionResponse = isInspection(consultation);
  const isOverdueResponse = isOverdue(consultation);
  const isRespondedStatus = consultation?.status === 'Responded';
  const response = responsesData?.responses.find(
    (resp) => !resp.submittedDate && resp.consultationId === consultationId
  );
  const isRpcInspection = isRpcResponse || isInspectionResponse;
  const canSubmitIfLateResponse =
    !isOverdueResponse ||
    (!!reasonForLateResponseId &&
      (!reasonForLateReasonIsOther || !!reasonForLateResponseDetail));

  const buildResponseRequest = () => {
    return {
      consultationId: response?.consultationId ?? 0,
      responseId: response?.id ?? 0,
      responseTypeId,
      responseDetail,
      reasonForLateResponseId,
      reasonForLateResponseDetail,
      documents: [
        ...(responseFiles?.map((file) => ({
          name: file.file.name,
          path: file.uri,
          response: true,
        })) ?? []),
        ...(supportingFiles?.map((file) => ({
          name: file.file.name,
          path: file.uri,
          response: false,
        })) ?? []),
      ],
    };
  };

  const submitResponse = () => {
    if (!response) return;
    submitResponseApi(buildResponseRequest(), {
      onSuccess: () => {
        resetForm();
        banner({
          id: 'submit-response-banner',
          title: t('response_submitted', 'Response submitted successfully'),
          duration: BANNER_TIMEOUT,
        });
        navigateToDashboard();
      },
      onError: () =>
        banner({
          id: 'submit-response-failed-banner',
          title: t('response_submission_failed', 'Response submission failed'),
          duration: BANNER_TIMEOUT,
          status: 'error',
        }),
    });
  };

  const filterResponses = (responses: GetResponsesConsultationResponse[] | undefined) => {
    return (
      responses
        ?.filter((resp) => resp.submittedDate)
        .flatMap((resp) => resp.answers)
        .sort(sortAnswers) ?? []
    );
  };

  const submittedAnswers = isRpcInspection ? filterResponses(responsesData?.responses) : null;

  const otherSubmittedAnswers = isRpcInspection
    ? filterResponses(responsesData?.otherResponses)
    : null;

  const pastAnswers = submittedAnswers?.filter((answer) =>
    consulteeOrganisationIds.includes(answer.organisation?.id)
  );

  const answers = consultation
    ? getAnswers(consultation, response, consulteeId, consulteeName)
    : [];

  const pastResponses = getPastResponses(responsesData, getResponseTypeDescription);

  const canSubmit =
    consultation && (isRpcResponse || isInspectionResponse)
      ? canSubmitAnswers(isRespondedStatus, consultation, canSubmitIfLateResponse, answers)
      : canSubmitStandard(
          isRespondedStatus,
          canSubmitIfLateResponse,
          responseTypeId,
          responseTypeIdIsConsultedInError,
          responseDetail,
          responseFiles,
          requiredFiles
        );

  return {
    isLoading: isLoadingConsultation || isLoadingResponse || !consultation || !responsesData,
    response,
    pastResponses,
    answers: answers ?? [],
    pastAnswers: pastAnswers ?? [],
    otherAnswers: otherSubmittedAnswers ?? [],
    canSubmit,
    canSubmitIfLateResponse,
    submitResponse,
    resetForm,
  };
};
